import { cn } from '@/lib/utils';
import { RefObject, useCallback, useEffect, useRef } from 'react';

import {
    FluidHoverGroup,
    useFluidHoverGroupItem
} from '@/components/ui/fluid-hover-group';

import { DisplayBrief } from './BriefCard';

const ACTIVE_LOOKAHEAD = 2;

interface FeedJumpNavProps {
    briefs: DisplayBrief[];
    activeId: string;
    onActiveIdChange: (id: string) => void;
    // The right-hand scrollable column this nav jumps to / spies on.
    scrollContainerRef: RefObject<HTMLDivElement>;
}

export function FeedJumpNav({
    briefs,
    activeId,
    onActiveIdChange,
    scrollContainerRef
}: FeedJumpNavProps) {
    const leftNavRef = useRef<HTMLDivElement>(null);

    // Scroll-spy using IntersectionObserver. `entries` only carries targets
    // whose intersection state changed since the last callback, in no
    // guaranteed order — during a transition both the outgoing and incoming
    // card can show up in the same batch. Tracking every currently-
    // intersecting target ourselves and picking the topmost one keeps the
    // choice deterministic instead of flip-flopping to whichever entry
    // happened to come first in that batch.
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const intersecting = new Map<string, number>();

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const id = entry.target.getAttribute('data-brief-id');
                    if (!id) continue;
                    if (entry.isIntersecting) {
                        intersecting.set(id, entry.boundingClientRect.top);
                    } else {
                        intersecting.delete(id);
                    }
                }

                let topId: string | null = null;
                let topValue = Infinity;
                for (const [id, top] of intersecting) {
                    if (top < topValue) {
                        topId = id;
                        topValue = top;
                    }
                }
                if (topId) onActiveIdChange(topId);
            },
            { root: container, rootMargin: '-140px 0px -50% 0px', threshold: 0 }
        );

        container
            .querySelectorAll('[data-brief-id]')
            .forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, [briefs, scrollContainerRef, onActiveIdChange]);

    // Keep the highlighted item in view as the scroll-spy above updates
    // activeId, since scrolling the right column alone doesn't move this list.
    // Deliberately not Element.scrollIntoView(): it walks every scrollable
    // ancestor (anything with overflow != visible, including this page's own
    // overflow-hidden shell) and can end up nudging their scroll position
    // too, dragging the sticky feed header along with it. Scrolling only
    // this container's own scrollTop keeps the effect local to the nav list.
    useEffect(() => {
        const container = leftNavRef.current;
        if (!container || !activeId) return;

        const items = Array.from(
            container.querySelectorAll<HTMLElement>('[data-nav-id]')
        );
        const index = items.findIndex(
            (item) => item.getAttribute('data-nav-id') === activeId
        );
        if (index === -1) return;

        const lookahead =
            items[Math.min(index + ACTIVE_LOOKAHEAD, items.length - 1)];
        const lookbehind = items[Math.max(index - ACTIVE_LOOKAHEAD, 0)];

        const containerRect = container.getBoundingClientRect();
        const aheadBottom = lookahead.getBoundingClientRect().bottom;
        const behindTop = lookbehind.getBoundingClientRect().top;

        let delta = 0;
        if (behindTop < containerRect.top) {
            delta = behindTop - containerRect.top;
        } else if (aheadBottom > containerRect.bottom) {
            delta = aheadBottom - containerRect.bottom;
        }
        if (delta !== 0) {
            container.scrollBy({ top: delta, behavior: 'smooth' });
        }
    }, [activeId]);

    const scrollTo = useCallback(
        (id: string) => {
            const container = scrollContainerRef.current;
            const target = container?.querySelector(`[data-brief-id="${id}"]`);
            if (!target || !container) return;

            // getBoundingClientRect (not offsetTop) so this stays correct
            // regardless of which ancestor ends up as the offsetParent.
            const targetTop =
                (target as HTMLElement).getBoundingClientRect().top -
                container.getBoundingClientRect().top +
                container.scrollTop;
            const startTop = container.scrollTop;
            const delta = targetTop - startTop - 16;
            const duration = 420;
            const start = performance.now();

            const ease = (t: number) =>
                t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

            const tick = (now: number) => {
                const elapsed = now - start;
                const progress = Math.min(elapsed / duration, 1);
                container.scrollTop = startTop + delta * ease(progress);
                if (progress < 1) requestAnimationFrame(tick);
            };

            requestAnimationFrame(tick);
            onActiveIdChange(id);
        },
        [scrollContainerRef, onActiveIdChange]
    );

    return (
        <div className="hidden lg:flex flex-col w-88 h-full overflow-hidden shrink-0">
            <FluidHoverGroup
                ref={leftNavRef}
                className="flex flex-col flex-1 gap-1 pt-2 pr-4 pb-6 pl-1 overflow-y-auto overscroll-contain"
            >
                {briefs.map((brief) => (
                    <FeedJumpNavItem
                        key={brief.id}
                        brief={brief}
                        isActive={brief.id === activeId}
                        onSelect={scrollTo}
                    />
                ))}
            </FluidHoverGroup>
        </div>
    );
}

interface FeedJumpNavItemProps {
    brief: DisplayBrief;
    isActive: boolean;
    onSelect: (id: string) => void;
}

function FeedJumpNavItem({ brief, isActive, onSelect }: FeedJumpNavItemProps) {
    const itemRef = useFluidHoverGroupItem<HTMLButtonElement>(undefined);

    return (
        <button
            ref={itemRef}
            data-nav-id={brief.id}
            onClick={() => onSelect(brief.id)}
            className={cn(
                'flex flex-col gap-2 px-4 py-3.5 rounded-xl w-full text-left transition-colors shrink-0',
                isActive && 'bg-sidebar-accent'
            )}
        >
            <div className="flex items-center gap-2 min-w-0">
                <div
                    className="flex justify-center items-center rounded-md size-5 font-semibold text-[10px] text-white shrink-0"
                    style={{ backgroundColor: brief.agentColor }}
                >
                    {brief.avatarLabel[0]}
                </div>
                <span className="flex-1 min-w-0 text-muted-foreground text-sm truncate">
                    {brief.agentName}
                </span>
                {brief.isUnread && (
                    <span
                        className="bg-notification rounded-full size-2 shrink-0"
                        aria-label="New"
                    />
                )}
                <span className="text-muted-foreground text-sm tabular-nums shrink-0">
                    {brief.timeAgo}
                </span>
            </div>
            <div
                className={cn(
                    'font-medium text-(length:--fs-title) text-foreground/85 leading-normal line-clamp-2',
                    isActive && 'text-foreground'
                )}
            >
                {brief.headline}
            </div>
        </button>
    );
}
